import { Component, inject, signal, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AdminService } from '../../../core/services/admin.service';
import { Data } from '../../../core/services/data';
import { FileItem } from '../../../core/models/file';

@Component({
  selector: 'app-file-upload',
  standalone: true,
  imports: [FormsModule, CommonModule],
  templateUrl: './file-upload.html',
  styleUrl: './file-upload.css'
})
export class FileUpload implements OnInit {
  private adminService = inject(AdminService);
  private dataService = inject(Data);

  selectedFile = signal<File | null>(null);
  selectedCategory = signal<'ausschreibungen' | 'ergebnisse'>('ausschreibungen');
  fileTitle = '';
  fileDescription = '';
  isUploading = signal<boolean>(false);
  errorMessage = signal<string>('');
  successMessage = signal<string>('');

  // File lists
  ausschreibungenFiles = signal<FileItem[]>([]);
  ergebnisseFiles = signal<FileItem[]>([]);
  isLoadingFiles = signal<boolean>(false);

  // Delete confirmation
  showDeleteConfirm = signal<boolean>(false);
  fileToDelete = signal<{ category: string; filename: string } | null>(null);

  categories = [
    { value: 'ausschreibungen' as const, label: 'Ausschreibungen' },
    { value: 'ergebnisse' as const, label: 'Ergebnisse' }
  ];

  onFileSelected(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      const file = target.files[0];
      const allowedTypes = [
        'application/pdf',
        'application/vnd.ms-excel', // .xls
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' // .xlsx
      ];

      if (allowedTypes.includes(file.type)) {
        this.selectedFile.set(file);
        this.errorMessage.set('');
      } else {
        this.errorMessage.set('Nur PDF- und Excel-Dateien (.pdf, .xls, .xlsx) sind erlaubt');
        this.selectedFile.set(null);
      }
    }
  }

  selectCategory(category: 'ausschreibungen' | 'ergebnisse') {
    this.selectedCategory.set(category);
  }

  uploadFile() {
    const file = this.selectedFile();
    if (!file) {
      this.errorMessage.set('Bitte wählen Sie eine Datei aus');
      return;
    }

    if (!this.fileTitle.trim()) {
      this.errorMessage.set('Bitte geben Sie einen Titel ein');
      return;
    }

    this.isUploading.set(true);
    this.errorMessage.set('');

    this.adminService.uploadFile(file, this.selectedCategory(), this.fileTitle, this.fileDescription).subscribe({
      next: (response) => {
        this.successMessage.set(response.message);
        this.isUploading.set(false);
        this.resetForm();
        this.loadFiles(); // Reload files after upload
        setTimeout(() => this.successMessage.set(''), 3000);
      },
      error: (error) => {
        this.errorMessage.set(error.error?.message || 'Fehler beim Hochladen');
        this.isUploading.set(false);
      }
    });
  }

  resetForm() {
    this.selectedFile.set(null);
    this.fileTitle = '';
    this.fileDescription = '';
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  ngOnInit() {
    this.loadFiles();
  }

  loadFiles() {
    this.isLoadingFiles.set(true);

    // Load Ausschreibungen
    this.dataService.getAusschreibungen().subscribe({
      next: (files) => {
        this.ausschreibungenFiles.set(files);
      },
      error: (error) => {
        console.error('Error loading Ausschreibungen:', error);
      }
    });

    // Load Ergebnisse
    this.dataService.getErgebnisse().subscribe({
      next: (files) => {
        this.ergebnisseFiles.set(files);
        this.isLoadingFiles.set(false);
      },
      error: (error) => {
        console.error('Error loading Ergebnisse:', error);
        this.isLoadingFiles.set(false);
      }
    });
  }

  confirmDelete(category: string, filename: string) {
    this.fileToDelete.set({ category, filename });
    this.showDeleteConfirm.set(true);
  }

  cancelDelete() {
    this.showDeleteConfirm.set(false);
    this.fileToDelete.set(null);
  }

  deleteFile() {
    const file = this.fileToDelete();
    if (!file) return;

    this.adminService.deleteFile(file.category, file.filename).subscribe({
      next: (response) => {
        this.successMessage.set(response.message || 'Datei erfolgreich gelöscht');
        this.loadFiles();
        setTimeout(() => this.successMessage.set(''), 3000);
        this.showDeleteConfirm.set(false);
        this.fileToDelete.set(null);
      },
      error: (error) => {
        this.errorMessage.set(error.error?.message || 'Fehler beim Löschen der Datei');
        this.showDeleteConfirm.set(false);
        this.fileToDelete.set(null);
      }
    });
  }
}
